import { SocialNetwork } from '../../../../components/socialProfiles/SocialNetwork';
import { Icon } from '../../../../components/images/Icon/Icon';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { IconsEnum } from '../../../../constants/icons';
import { useDragOver } from '@minoru/react-dnd-treeview';
import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import classes from '../TreeMenu/TreeMenu.module.scss';
import { TreeMenuItemType } from '../../../../models/find-content';
import { RssIcon } from '../RssIcon';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';

export type TreeItemProps = {
  item: TreeMenuItemType;
  depth: number;
  isOpen: boolean;
  onToggle: (id: number | string) => void;
  hasDivider?: boolean;
};

export const TreeItemView = (props: TreeItemProps): JSX.Element => {
  const { item, isOpen, hasDivider } = props;
  const [menuOpen, setMenuOpen] = React.useState(false);

  const { setSelectedMenuItem, sources } = useContext(TreeMenuContext);

  const icon =
    item.type === DraggableTypesObject.MENU_TREE_SOURCE && !item.icon && item.provider !== 'RSS' ? (
      <SocialNetwork
        {...{
          provider: item.provider,
          network: item.network,
          size: 'small',
          type: 'circle'
        }}
      />
    ) : item?.icon ? (
      <Icon name={item.icon} size={'medium'} />
    ) : item.type === DraggableTypesObject.MENU_TREE_SOURCE && item.provider === 'RSS' ? (
      <RssIcon size={'small'} type={'circle'} />
    ) : undefined;

  const threeDotMenu = item.hasMenu ? (
    <ButtonMenu
      isSquareButton={false}
      variantOnOpen={'clear'}
      side={'right'}
      icon={IconsEnum.threeDot}
      color={ButtonColorsEnum.grey}
      size={'medium'}
      variant={'clear'}
      listItems={item.menuItems}
      closeOnMouseOut={false}
      onOpenChange={setMenuOpen}
    />
  ) : (
    <></>
  );

  // Used to open/close folders
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.onToggle(item.id);
  };

  const handleSourceClick = useCallback(
    (item: TreeMenuItemType) => {
      if (item.type === DraggableTypesObject.MENU_TREE_SOURCE) {
        // Remove the 'source-' prepend
        const sourceId = item.id.substring(7);
        const source = sources.find(s => s.id === sourceId);

        setSelectedMenuItem(source);
      }
    },
    [setSelectedMenuItem, sources]
  );

  const itemClasses = classNames(classes.TreeMenuItem, {
    [classes.TreeMenuItemAction]: item.type === DraggableTypesObject.MENU_TREE_ACTION,
    [classes.TreeMenuItemFolder]: item.type === DraggableTypesObject.MENU_TREE_FOLDER,
    [classes.TreeMenuItemSocial]: item.type === DraggableTypesObject.MENU_TREE_SOURCE,
    [classes.TreeMenuItemFolderOpen]: item.type === DraggableTypesObject.MENU_TREE_FOLDER && isOpen
  });

  const iconClasses = classNames({
    [classes.SocialIcon]: item.type === DraggableTypesObject.MENU_TREE_SOURCE,
    [classes.StandardIcon]: item.type !== DraggableTypesObject.MENU_TREE_SOURCE
  });

  const labelWrapperClasses = classNames('cell shrink one-liner', classes.LabelWrapper);
  const treeMenuItemContainer = classNames('grid-x align-left', classes.TreeMenuItemContainer);
  const dotMenuWrapperClasses = classNames('cell shrink', classes.DotMenu, {
    [classes.DotMenuOpen]: menuOpen
  });

  const dragOverProps = useDragOver(item.id, props.isOpen, props.onToggle);
  const dragOver = useMemo(
    () => (item.type !== DraggableTypesObject.MENU_TREE_ACTION ? dragOverProps : {}),
    [dragOverProps, item]
  );

  return (
    <>
      <div className={itemClasses} {...dragOver}>
        <div className={'grid-x align-justify'}>
          <div
            className={classNames('cell grow', {
              [classes.TreeMenuItemWrapper]: true,
              [classes.HoverContainer]: item.type !== DraggableTypesObject.MENU_TREE_ACTION
            })}
            onClick={
              item.type === DraggableTypesObject.MENU_TREE_FOLDER ? handleToggle : () => handleSourceClick(item)
            }>
            <div className={treeMenuItemContainer} onClick={item.action}>
              {item.type === DraggableTypesObject.MENU_TREE_FOLDER && (
                <div className={iconClasses}>
                  {item.subItems && item.subItems?.length > 0 ? (
                    <Icon name={isOpen ? IconsEnum.arrowDown : IconsEnum.arrowRight} size={'medium'} />
                  ) : (
                    <svg width={18} height={18} />
                  )}
                </div>
              )}
              <div className={classes.ContentWrapper}>
                <div className={classNames('grid-x align-left', classes.TreeMenuItemContent)}>
                  {icon && <div className={iconClasses}>{icon}</div>}
                  <div className={labelWrapperClasses}>
                    <div
                      className={
                        item.type === DraggableTypesObject.MENU_TREE_FOLDER
                          ? classes.TreeMenuItemLabelFolder
                          : classes.TreeMenuItemLabel
                      }>
                      {item.label}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={dotMenuWrapperClasses}>{threeDotMenu}</div>
        </div>
      </div>
      {hasDivider && <hr className={classes.MenuDivider} />}
    </>
  );
};
